/**
 * KanbanBoard — Monday-style DnD pipeline view (Phase 2, 2026-05-16).
 *
 * Native HTML5 drag-drop (no external libs) — kolumny per stage,
 * cards: tytuł / inwestor / miasto / completion bar / specced badge.
 *
 * Optimistic update — UI od razu pokazuje przesunięcie, API call w tle.
 * Rollback przy błędzie. Toast confirmacji po sukcesie.
 *
 * Props:
 *   items[]               — listy zwracane z /api/investments (z investment_stage)
 *   stages[]              — ['discovered', 'researched', …]
 *   stageMeta             — { stage_key: { label, color, icon? } }
 *   onMove(itemId, from, to) — async function (rzucane przy DnD)
 *   onItemClick(item)     — open detail
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Building2, MapPin, CheckCircle2, ArrowRight, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';


function fmtPLN(v) {
  if (!v || v === 0) return null;
  return new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 }).format(v) + ' PLN';
}


function KanbanCard({ inv, onClick, onDragStart, dragging }) {
  const chainPct = inv.chain_total_count
    ? Math.round((inv.chain_filled_count / inv.chain_total_count) * 100)
    : 0;
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, inv)}
      onClick={onClick}
      className={`bg-card border border-border rounded-md p-2.5 cursor-grab active:cursor-grabbing
                  hover:border-primary/40 transition-colors text-xs space-y-1.5
                  ${dragging ? 'opacity-40' : ''}`}
      data-testid={`kanban-card-${inv.source_id}`}
    >
      <div className="flex items-center gap-1 flex-wrap">
        {inv.construction_phase && inv.construction_phase !== 'unknown' && (
          <Badge
            variant="outline"
            className="text-[9px] h-4 px-1 border-border/60"
            title={inv.construction_phase_meta?.reason}
          >
            🏗 {({
              planning: 'Plan', design: 'Projekt 🎯', construction: 'Realizacja',
              fit_out: 'Wykończ.', completed: 'Koniec',
            })[inv.construction_phase] || inv.construction_phase}
          </Badge>
        )}
        {inv.match_score >= 70 && (
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[9px] h-4 px-1">
            match {inv.match_score}
          </Badge>
        )}
        {inv.specced_products?.length > 0 && (
          <Badge className="bg-primary/15 text-primary border-primary/40 text-[9px] h-4 px-1 gap-0.5">
            <CheckCircle2 className="w-2.5 h-2.5" />
            {inv.specced_products.length} spec
          </Badge>
        )}
        {inv.linked_tender_id && (
          <Badge variant="outline" className="text-[9px] h-4 px-1 gap-0.5">
            <ArrowRight className="w-2.5 h-2.5" />
            przetarg
          </Badge>
        )}
      </div>
      <div className="font-medium text-foreground/95 line-clamp-2">
        {inv.title || '(brak tytułu)'}
      </div>
      <div className="text-muted-foreground space-y-0.5">
        {inv.investor_name && (
          <div className="flex items-center gap-1 truncate">
            <Building2 className="w-3 h-3 shrink-0" />
            <span className="truncate">{inv.investor_name}</span>
          </div>
        )}
        {inv.city && (
          <div className="flex items-center gap-1 truncate">
            <MapPin className="w-3 h-3 shrink-0" />
            <span className="truncate">{inv.city}{inv.region ? `, ${inv.region}` : ''}</span>
          </div>
        )}
        {fmtPLN(inv.estimated_value) && (
          <div className="font-medium text-foreground/80">{fmtPLN(inv.estimated_value)}</div>
        )}
      </div>
      <div className="flex items-center gap-1.5 pt-1 border-t border-border/50">
        <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${chainPct}%` }}
          />
        </div>
        <span className="text-[9px] text-muted-foreground">
          {inv.chain_filled_count}/{inv.chain_total_count}
        </span>
      </div>
    </div>
  );
}


function KanbanColumn({
  stage, meta, items, onDrop, onDragOver, onItemClick, onDragStart,
  draggingId, hovered, busy,
}) {
  const Icon = meta?.icon;
  return (
    <div
      onDragOver={(e) => onDragOver(e, stage)}
      onDrop={(e) => onDrop(e, stage)}
      className={`flex-1 min-w-[240px] max-w-[300px] bg-muted/20 rounded-lg p-2 flex flex-col
                  ${hovered ? 'ring-2 ring-primary/40' : ''}`}
      data-testid={`kanban-col-${stage}`}
    >
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon className="w-3.5 h-3.5" />}
          <span className="text-xs font-semibold">{meta?.label || stage}</span>
          <Badge variant="outline" className="text-[10px] h-4 px-1">{items.length}</Badge>
        </div>
        {busy && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto max-h-[calc(100vh-280px)] pr-1">
        {items.length === 0 ? (
          <div className="text-[10px] text-muted-foreground text-center py-6 italic">
            (pusta kolumna — przeciągnij kartę tutaj)
          </div>
        ) : (
          items.map((inv) => (
            <KanbanCard
              key={inv.source_id}
              inv={inv}
              dragging={draggingId === inv.source_id}
              onDragStart={onDragStart}
              onClick={() => onItemClick && onItemClick(inv)}
            />
          ))
        )}
      </div>
    </div>
  );
}


export default function KanbanBoard({
  items = [],
  stages = [],
  stageMeta = {},
  onMove,
  onItemClick,
  busyStages = {},
}) {
  const [draggingId, setDraggingId] = useState(null);
  const [draggingFromStage, setDraggingFromStage] = useState(null);
  const [hoveredStage, setHoveredStage] = useState(null);
  const [optimisticItems, setOptimisticItems] = useState(null);

  const effectiveItems = optimisticItems || items;

  // Group by stage
  const grouped = useMemo(() => {
    const m = {};
    stages.forEach((s) => { m[s] = []; });
    for (const inv of effectiveItems) {
      const s = inv.investment_stage || stages[0];
      if (m[s]) m[s].push(inv);
      else m[stages[0]].push(inv);
    }
    return m;
  }, [effectiveItems, stages]);

  const handleDragStart = useCallback((e, inv) => {
    setDraggingId(inv.source_id);
    setDraggingFromStage(inv.investment_stage || stages[0]);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', inv.source_id); } catch { /* */ }
  }, [stages]);

  const handleDragOver = useCallback((e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (hoveredStage !== stage) setHoveredStage(stage);
  }, [hoveredStage]);

  const handleDrop = useCallback(async (e, toStage) => {
    e.preventDefault();
    setHoveredStage(null);
    const itemId = draggingId;
    const fromStage = draggingFromStage;
    setDraggingId(null);
    setDraggingFromStage(null);

    if (!itemId || !toStage || fromStage === toStage) return;

    // Optimistic update
    const next = (items || []).map((x) =>
      x.source_id === itemId ? { ...x, investment_stage: toStage } : x
    );
    setOptimisticItems(next);

    try {
      await onMove(itemId, fromStage, toStage);
      toast.success(`${stageMeta[fromStage]?.label || fromStage} → ${stageMeta[toStage]?.label || toStage}`);
      // After success — clear optimistic (parent will reload)
      setOptimisticItems(null);
    } catch (err) {
      toast.error('Nie udało się zmienić etapu');
      setOptimisticItems(null); // rollback
      // eslint-disable-next-line no-console
      console.warn('Kanban move failed:', err);
    }
  }, [draggingId, draggingFromStage, items, onMove, stageMeta]);

  return (
    <div className="flex gap-2 overflow-x-auto pb-2" data-testid="kanban-board">
      {stages.map((stage) => (
        <KanbanColumn
          key={stage}
          stage={stage}
          meta={stageMeta[stage]}
          items={grouped[stage] || []}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onDragStart={handleDragStart}
          onItemClick={onItemClick}
          draggingId={draggingId}
          hovered={hoveredStage === stage}
          busy={busyStages[stage]}
        />
      ))}
    </div>
  );
}
